import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  dimensionRowLimitSelector,
  getFirstAppliedFilter,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import { chartIntervalSelector, noBrushSelector, tableCompareEnabledSelector, tableCompareIntervalSelector } from '@store/trends/birdChart/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { ERROR_DIMENSION_MAP } from '@utils/dimension/errorCodeHelper';
import { INVERTED_METRIC_SWITCH_MAP } from '@utils/metrics';
import useFetchGroupBy from './useFetchGroupBy';
import {
  formatData,
  formatErrorColumns,
  formatMultiErrorColumns,
  formatMultiData,
  getMetricFilterMetric,
} from '../dimension-table/utils';
import useSetErrorDimensionExport from './useSetErrorDimensionExport';

function useErrorDimension({ dimension, unit, enabled = true }) {
  const filter = useSelector(getFirstAppliedFilter);
  const interval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);

  const { errorLimit } = useSelector(dimensionRowLimitSelector);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const noBrushSelected = useSelector(noBrushSelector);

  const errorCodeInfo = ERROR_DIMENSION_MAP[dimension];
  const { baseline, primary, pctMetricKey } = errorCodeInfo;

  let comparisonInterval = useSelector(tableCompareIntervalSelector);
  const isTableCompareEnabled = useSelector(tableCompareEnabledSelector);

  const [sortBy, setSortBy] = useState({ key: primary, order: 'desc' });
  const onColumnSort = ({ key, order }) => {
    setSortBy({ key, order });
  };

  const [searchText, onSearch] = useState('');

  const metrics = [baseline, primary];

  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension,
    metrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric: primary,
    limit: errorLimit,
    realTimeOnTrends,
    noBrushSelected,
    comparisonInterval,
    isTableCompareEnabled
  });

  let rows = [];
  let totals = {};
  let columns = [];
  let pctShareMaxValues = {};
  const metric = unit === '%' ? pctMetricKey : primary;

  // TODO: improve formatting
  if ('metricFilterData' in data) {
    ({ rows, totals, pctShareMaxValues } = formatMultiData({
      data,
      sortBy,
      searchText,
      metrics: [metric],
      hideUnknownRows: true,
    }));
    columns = formatMultiErrorColumns({
      dimension,
      metric,
      errorCodeInfo,
      realTimeOnTrends,
      showTrend: isTableCompareEnabled
    });
  } else {
    ({ rows, totals, pctShareMaxValues } = formatData({
      data,
      sortBy,
      searchText,
      hideUnknownRows: true,
    }));
    columns = formatErrorColumns({ dimension, metric, errorCodeInfo, realTimeOnTrends, showTrend: isTableCompareEnabled });
  }

  // count value is based on error metric (vsf, vpf), but the % toggle value for these are coming up as 100 for each row
  // for % toggle we switch to secondary metric and calculate value locally (Ended Plays, Attempts)
  // totals would be 100 in that case but for totals the error metric has valid values
  // hence this hack to swtich only totals for main and secondary filter
  try {
    const primaryPercentageMetric =
      primary in INVERTED_METRIC_SWITCH_MAP ? INVERTED_METRIC_SWITCH_MAP[primary] : metric;
    totals[pctMetricKey] = totals[primaryPercentageMetric];
    if (metricFilter.length > 0) {
      const mfMetric = getMetricFilterMetric(primaryPercentageMetric);
      const mfPctMetric = getMetricFilterMetric(pctMetricKey);
      totals[mfPctMetric] = totals[mfMetric];
    }
  } catch (err) {
    // ignore
  }

  useSetErrorDimensionExport({ dimension, errorCodeInfo, data: rows, totals: { ...totals } });

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals: { ...totals },
    data: rows,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    pctShareMaxValues,
  };
}

export default useErrorDimension;
